import { ICopyWithBusinessRef } from '../../contracts/copy-with-business-ref.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { Fee } from './fee.js';
import { Product } from './product.js';

export class ProductOrderItem extends DataModel implements ICopyWithBusinessRef {
  static readonly TYPE_ID: string = 'fac8d656b3ac49e8a83c52bc7dbf25e4';
  static readonly SCHEMA_VERSION: number = 2;

  public readonly productId: string;

  private constructor(
    public readonly product: Product,
    public readonly quantity: number,
    public applicationFee: Fee | null, // Fee taken by In Time on top of the product price.
  ) {
    super();
    this.productId = product.uniqueId;
  }

  get $typeId(): string {
    return ProductOrderItem.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, ProductOrderItem.SCHEMA_VERSION);
  }

  static create(args: {
    product: Product,
    quantity: number,
    applicationFee: Fee | null,
  }): ProductOrderItem {
    return new ProductOrderItem(args.product, args.quantity, args.applicationFee);
  }

  static fromMap(context: SerializationContext, map: SerializedData): ProductOrderItem {
    return new ProductOrderItem(
        context.fromData<Product>(MapUtils.getRaw(map, 'product') as SerializedData),
        MapUtils.get<number>(map, 'quantity'),
        context.fromDataOrNull<Fee>(MapUtils.getOrNull<SerializedData>(map, 'applicationFee')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'product': context.toData(this.product),
      'productId': this.product.uniqueId,
      'quantity': this.quantity,
      'applicationFee': context.toDataOrNull(this.applicationFee),
    };
  }

  clone(): this {
    return new ProductOrderItem(
        this.product.clone(),
        this.quantity,
        this.applicationFee?.clone() ?? null,
    ) as this;
  }

  isSame(other: ProductOrderItem): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.product, this.product) &&
        isSameData(other.applicationFee, this.applicationFee) &&
        other.quantity === this.quantity;
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new ProductOrderItem(
        this.product.copyWithBusinessRef(businessRef),
        this.quantity,
        this.applicationFee?.clone() ?? null,
    ) as this;
  }

  static generateProductInstanceId(orderItemIndex: number, productIndex: number): string {
    // I assumed we will have less than 65K product types per order and less than 65K quantity per product type
    // which seems reasonable to me but...you know :)
    return `${orderItemIndex.toString(16).padStart(4, '0')}${productIndex.toString(16).padStart(4, '0')}`;
  }
}

export class ProductOrderItemBuilder extends DataModelBuilder<ProductOrderItem> {
  build(context: SerializationContext, map: SerializedData): ProductOrderItem {
    return ProductOrderItem.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(ProductOrderItem.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(ProductOrderItem.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In version 2 we added `applicationFee` of type `Fee | null`.
    if(version < 2) version = 2;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
